import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@babackapp/ui/components/accordion";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@babackapp/ui/components/alert-dialog";
import { ButtonGroup } from "@babackapp/ui/components/button-group";
import {
  ChevronDown,
  Copy,
  Earth,
  EarthIcon,
  Edit,
  ListFilter,
  Mail,
  MailIcon,
  MoreVertical,
  Rocket,
  Star,
  StarIcon,
} from "lucide-react";

import useGetAllShopEmailTemplatesSuspense from "@babackapp/private-api-client/client/use-get-all-shop-email-templates";
import useUpdateEmailTemplate from "@babackapp/private-api-client/client/use-update-email-template";
import { EmailTemplateDto } from "@babackapp/private-api-client/generated/schema";
import { Button } from "@babackapp/ui/components/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@babackapp/ui/components/dropdown-menu";
import {
  FileItem,
  FileItemAction,
  FileItemActions,
  FileItemDetails,
  FileItemInfos,
  FileItemTitle,
  FileItemTitleAnnotationIcon,
  FileItemTypeBadge,
  FileItemTypeIcon,
  FileList,
} from "@babackapp/ui/components/files";
import { InputGroup, InputGroupAddon, InputGroupInput } from "@babackapp/ui/components/input-group";
import { cn } from "@babackapp/ui/lib/utils";
import { PlusIcon, TrashIcon } from "@radix-ui/react-icons";
import { useTemplatesEditorRouting } from "../context/templates-editor-routing-context";
import { useTemplatesEditorConfig } from "../context/templates-editor-config-context";
import { useTranslation } from "react-i18next";
import { useMemo, useState } from "react";
import { CreateTemplateModal } from "./create-template-modal";
import useCurrentShopSuspense from "../hooks/use-current-shop-suspense";
import { useDeleteTemplate } from "../hooks/use-delete-template";
import { useDuplicateTemplate } from "../hooks/use-duplicate-template";
import { useSetDefaultTemplate } from "../hooks/use-set-default-template";
import { useTemplateActions } from "../hooks/use-template-actions";
import { TEMPLATE_CATEGORIES, useCategoryLabel } from "../lib/categories";
import { CategorySelection, CategorySelectionSubmenu } from "./category-selector";
import DependantWorkflows from "./dependant-workflows";
import { StatusSelectionModal } from "./empty-state/status-selection-modal";

type TemplatesListProps = {
  onTemplateClick?: () => void;
  onTemplateCreated?: () => void;
  onTemplateHover?: (template: EmailTemplateDto | null) => void;
  className?: string;
};

type TemplatesListStandaloneProps = TemplatesListProps;

export function TemplatesListStandalone(props: TemplatesListStandaloneProps) {
  return <TemplatesList {...props} />;
}

export function TemplatesList({
  onTemplateClick,
  onTemplateCreated,
  onTemplateHover,
  className,
}: TemplatesListProps) {
  const { t } = useTranslation("trackoffice");
  const getCategoryLabel = useCategoryLabel();
  const [templateType, setTemplateType] = useState<SearchTemplateType | null>(
    null,
  );
  const [category, setCategory] = useState<string | null>(null);

  const {
    shop,
    isCreateModalOpen,
    setIsCreateModalOpen,
    isStatusSelectionModalOpen,
    setIsStatusSelectionModalOpen,
    isGenerating,
    handleGenerateDefaultTemplates,
    navigateToTemplate,
    openCreateModal,
    openStatusSelectionModal,
  } = useTemplateActions({ onTemplateCreated });

  const { domain } = useTemplatesEditorConfig();
  const { data: templates = [] } = useGetAllShopEmailTemplatesSuspense(
    shop.id,
    domain,
  );
  const duplicateTemplate = useDuplicateTemplate();

  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    return templates.filter((template) => {
      const matchesSearch = template.title
        .toLowerCase()
        .includes(search.toLowerCase());
      const matchesType = !templateType || template.type === templateType;
      const matchesCategory = !category || template.category === category;
      return matchesSearch && matchesType && matchesCategory;
    });
  }, [templates, search, templateType, category]);

  const byCategory = useMemo(() => {
    const byCategory: Record<string, EmailTemplateDto[]> = {};

    for (const template of filtered) {
      const categoryId = template.category || "others";
      if (!byCategory[categoryId]) {
        byCategory[categoryId] = [];
      }
      byCategory[categoryId].push(template);
    }

    return byCategory;
  }, [filtered]);

  const categoriesWithTemplates = useMemo(() => {
    return TEMPLATE_CATEGORIES.filter((cat) => byCategory[cat.id]?.length > 0);
  }, [byCategory]);

  const isSearchActive = search.length > 0;
  const allCategoryIds = categoriesWithTemplates.map((cat) => cat.id);

  return (
    <div className={cn("flex relative flex-col h-full", className)}>
      <div className="z-50 flex-shrink-0 flex gap-1 items-center">
        <SearchBar
          resultsLength={filtered.length}
          search={search}
          setSearch={setSearch}
          templateType={templateType}
          setTemplateType={setTemplateType}
          category={category}
          setCategory={setCategory}
        />
      </div>

      <div className="z-0 pt-4 flex-1 overflow-auto">
        {categoriesWithTemplates.length > 0 && (
          <Accordion
            type="multiple"
            defaultValue={allCategoryIds}
            value={isSearchActive ? allCategoryIds : undefined}
          >
            {categoriesWithTemplates.map((cat) => (
              <AccordionItem key={cat.id} value={cat.id}>
                <AccordionTrigger>
                  {getCategoryLabel(cat.id)} ({byCategory[cat.id].length})
                </AccordionTrigger>
                <AccordionContent>
                  <FileList>
                    {byCategory[cat.id].map((template) => (
                      <TemplateItem
                        key={template.id}
                        template={template}
                        onClose={onTemplateClick}
                        onDuplicate={() => duplicateTemplate(template.id)}
                        onHover={onTemplateHover}
                      />
                    ))}
                  </FileList>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        )}
      </div>
      <div className="flex justify-end pt-4 border-t">
        <ButtonGroup>
          <Button
            onClick={openCreateModal}
            disabled={isGenerating}
          >
            <PlusIcon />
            {t(
              "templatesEditor.templatesList.addNewTemplate",
              "Ajouter un template",
            )}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="icon" disabled={isGenerating}>
                <ChevronDown />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem
                onSelect={openStatusSelectionModal}
              >
                <Rocket />{" "}
                {t(
                  "templatesEditor.emptyState.createButton",
                  "Create default templates",
                )}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </ButtonGroup>
      </div>

      <CreateTemplateModal
        open={isCreateModalOpen}
        onOpenChange={setIsCreateModalOpen}
        onTemplateCreated={(templateId) => {
          onTemplateCreated?.();
          navigateToTemplate(templateId);
        }}
      />

      <StatusSelectionModal
        open={isStatusSelectionModalOpen}
        onOpenChange={setIsStatusSelectionModalOpen}
        onConfirm={handleGenerateDefaultTemplates}
      />
    </div>
  );
}

type SearchTemplateType = "web" | "email";

function SearchBar({
  search,
  setSearch,
  templateType,
  setTemplateType,
  category,
  setCategory,
}: {
  search: string;
  setSearch: (value: string) => void;
  templateType: SearchTemplateType | null;
  setTemplateType: (value: SearchTemplateType | null) => void;
  category: string | null;
  setCategory: (value: string | null) => void;
  resultsLength: number;
}) {
  const toggleTemplateType = (type: SearchTemplateType) => {
    setTemplateType(templateType === type ? null : type);
  };

  return (
    <InputGroup>
      <InputGroupInput
        placeholder="Search..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      <InputGroupAddon>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <span className="inline-flex gap-1 items-center h-full">
              {((Component: React.FC<React.SVGProps<SVGSVGElement>>) => (
                <Component className="text-muted-foreground size-4" />
              ))(
                templateType === null
                  ? ListFilter
                  : {
                    web: EarthIcon,
                    email: MailIcon,
                  }[templateType],
              )}
              <ChevronDown className="size-3" />
            </span>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <DropdownMenuCheckboxItem
              checked={templateType === "web"}
              onSelect={() => toggleTemplateType("web")}
            >
              <span className="flex-1">Web</span>
              <Earth className="size-4 text-muted-foreground" />
            </DropdownMenuCheckboxItem>
            <DropdownMenuCheckboxItem
              checked={templateType === "email"}
              onSelect={() => toggleTemplateType("email")}
            >
              <span className="flex-1">Email</span>
              <Mail className="size-4 text-muted-foreground" />{" "}
            </DropdownMenuCheckboxItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </InputGroupAddon>
      <InputGroupAddon align="inline-end" className="inline-flex">
        <CategorySelection value={category} onChange={setCategory} />
      </InputGroupAddon>
    </InputGroup>
  );
}

function TemplateItem({
  template,
  onClose,
  onDuplicate,
  onHover,
}: {
  template: EmailTemplateDto;
  onClose?: () => void;
  onDuplicate: () => void;
  onHover?: (template: EmailTemplateDto | null) => void;
}) {
  const { t } = useTranslation("trackoffice");
  const { data: shop } = useCurrentShopSuspense();
  const { openTemplate } = useTemplatesEditorRouting();
  const { setDefaultTemplate, isDefaultTemplate } = useSetDefaultTemplate();
  const getCategoryLabel = useCategoryLabel();
  const {
    isDeleteDialogOpen,
    setIsDeleteDialogOpen,
    handleDeleteClick,
    openDeleteDialog,
    hasLinkedNotifications,
  } = useDeleteTemplate(template.id);
  const { mutate: updateTemplate } = useUpdateEmailTemplate(shop.id);

  const isDefault = isDefaultTemplate(template.id);

  const updateTemplateCategory = (
    templateId: string,
    category: string | null,
  ) => {
    updateTemplate({
      templateId,
      requestBody: {
        category: category,
      },
    });
  };

  const categoryLabel = template.category
    ? getCategoryLabel(template.category)
    : t("templatesEditor.templatesList.noCategory", "No category");

  return (
    <>
      <FileItem
        key={template.id}
        onMouseEnter={() => onHover?.(template)}
        onMouseLeave={() => onHover?.(null)}
      >
        <FileItemTypeIcon>
          {template.type === "email" ? <Mail /> : <EarthIcon />}
          <FileItemTypeBadge
            className={cn(
              "capitalize",
              template.type === "email" &&
              "bg-green-50 border-green-300 text-green-500",
              template.type === "web" &&
              "bg-amber-50 border-amber-300 text-amber-500",
            )}
          >
            {template.type}
          </FileItemTypeBadge>
        </FileItemTypeIcon>
        <a
          className="flex-1 cursor-pointer"
          onClick={() => {
            openTemplate?.(template.id);
            onClose?.();
          }}
        >
          <FileItemInfos>
            <FileItemTitle>
              {template.title}
              {isDefault && (
                <FileItemTitleAnnotationIcon tooltip="Template par défault">
                  <Star fill="var(--muted-foreground)" />
                </FileItemTitleAnnotationIcon>
              )}
            </FileItemTitle>
            <FileItemDetails className="inline-flex gap-1 items-center">
              {t("templatesEditor.templatesList.modifiedOn", "Modified on")}{" "}
              {new Date(template.updatedAt).toLocaleDateString()} |{" "}
              <span>{categoryLabel}</span>
            </FileItemDetails>
          </FileItemInfos>
        </a>
        <FileItemActions>
          <DependantWorkflows templateId={template.id} />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <FileItemAction tooltip="Actions">
                <MoreVertical />
              </FileItemAction>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-[200px]">
              <DropdownMenuItem
                onClick={() => {
                  openTemplate?.(template.id);
                  onClose?.();
                }}
              >
                <Edit />
                {t("templatesEditor.templatesList.editTemplate", "Edit")}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  onDuplicate();
                }}
              >
                <Copy />
                {t(
                  "templatesEditor.templatesList.duplicateTemplate",
                  "Duplicate",
                )}
              </DropdownMenuItem>
              {template.type === "web" && (
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    setDefaultTemplate(template.id);
                  }}
                >
                  <StarIcon />
                  {t(
                    "templatesEditor.templatesList.setDefaultTemplate",
                    "Set default",
                  )}
                </DropdownMenuItem>
              )}

              <CategorySelectionSubmenu
                value={template.category ?? null}
                onChange={(next) => updateTemplateCategory(template.id, next)}
              />

              <DropdownMenuSeparator />

              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  openDeleteDialog();
                }}
                disabled={hasLinkedNotifications}
                className={cn(
                  !hasLinkedNotifications &&
                  "text-destructive focus:text-destructive",
                  hasLinkedNotifications && "opacity-50 cursor-not-allowed",
                )}
              >
                <div className="flex flex-col gap-2">
                  <span className="inline-flex gap-1 items-center">
                    <TrashIcon className="size-4" />
                    {t(
                      "templatesEditor.templatesList.deleteTemplate",
                      "Delete",
                    )}
                  </span>
                  {hasLinkedNotifications && (
                    <p className="text-xs text-muted-foreground">
                      {t(
                        "templatesEditor.templatesList.cannotDeleteLinkedTemplate",
                        `You cannot delete a template with linked workflow. Please unlink all workflows first.`,
                      )}
                    </p>
                  )}
                </div>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </FileItemActions>
      </FileItem>

      <AlertDialogForDelete
        isOpen={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        onConfirm={handleDeleteClick}
      />
    </>
  );
}

function AlertDialogForDelete({
  isOpen,
  onOpenChange,
  onConfirm,
}: {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
}) {
  const { t } = useTranslation("trackoffice");

  return (
    <AlertDialog open={isOpen} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t(
              "templatesEditor.deleteTemplateDialog.title",
              "Delete template?",
            )}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {t(
              "templatesEditor.deleteTemplateDialog.description",
              "Are you sure you want to delete this template? This action cannot be undone.",
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>
            {t("templatesEditor.deleteTemplateDialog.cancel", "Cancel")}
          </AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm}>
            {t("templatesEditor.deleteTemplateDialog.confirm", "Delete")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
